import ChartBarIcon from '@heroicons/react/24/solid/ChartBarIcon';
import UsersIcon from '@heroicons/react/24/solid/UsersIcon';
import BankNotes from '@heroicons/react/24/solid/BanknotesIcon';
import NewspaperIcon from '@heroicons/react/24/solid/NewspaperIcon';
import QuestionMarkIcon from '@heroicons/react/24/solid/QuestionMarkCircleIcon';
import { SvgIcon } from '@mui/material';
import type { NavEntryGroup, NavEntryItem } from '@starjack/layout';
import {
    ArrowTrendingUpIcon,
    CurrencyEuroIcon,
    DocumentDuplicateIcon,
    GiftIcon,
    GiftTopIcon,
    KeyIcon,
    Squares2X2Icon,
    TicketIcon,
    UserGroupIcon,
    WrenchScrewdriverIcon,
} from '@heroicons/react/24/solid';
import { Permission } from '@/models/user';
import { useTranslation } from 'react-i18next';

export type AdminNavEntryItem = Omit<NavEntryItem, 'permissionsRequired'> & {
    permissionsRequired: Permission[];
};
export type AdminNavEntryGroup = Omit<NavEntryGroup, 'entries'> & {
    entries: AdminNavEntryItem[];
};
export type AdminSidebarItem = AdminNavEntryGroup | AdminNavEntryItem;

type UseSidebarParams = {
    disabledPaths?: string[];
    liftOperatorId?: string;
};

export const useSidebarItems = (
    params?: UseSidebarParams
): AdminSidebarItem[] => {
    const { t } = useTranslation();

    return [
        {
            title: t('sidebar.dashboard'),
            icon: (
                <SvgIcon fontSize='small'>
                    <ChartBarIcon />
                </SvgIcon>
            ),
            entries: [
                {
                    title: t('sidebar.allChannels'),
                    path: '/',
                    icon: null,
                    permissionsRequired: [Permission.STATISTIC_NAV],
                    exact: true,
                },
                {
                    title: t('sidebar.starjackPortal'),
                    path: '/dashboard/starjack-portal',
                    icon: null,
                    permissionsRequired: [Permission.STATISTIC_NAV],
                    exact: true,
                },
                {
                    title: t('sidebar.webshop'),
                    path: '/dashboard/webshop',
                    icon: null,
                    permissionsRequired: [Permission.STATISTIC_NAV],
                    exact: true,
                },
                {
                    title: t('sidebar.hospitality'),
                    path: '/dashboard/hospitality',
                    icon: null,
                    permissionsRequired: [Permission.STATISTIC_NAV],
                    exact: true,
                },
                {
                    title: t('sidebar.b2b'),
                    path: '/dashboard/b2b',
                    icon: null,
                    permissionsRequired: [Permission.STATISTIC_NAV],
                    exact: true,
                },
            ],
        },
        {
            title: t('sidebar.generalSettings'),
            path: '/einstellungen',
            permissionsRequired: [Permission.GENERAL_SETTINGS_NAV],
            icon: (
                <SvgIcon fontSize='small'>
                    <WrenchScrewdriverIcon />
                </SvgIcon>
            ),
        },
        {
            title: t('sidebar.sales'),
            icon: (
                <SvgIcon fontSize='small'>
                    <ArrowTrendingUpIcon />
                </SvgIcon>
            ),
            entries: [
                {
                    title: t('sidebar.customers'),
                    path: '/kunden',
                    icon: (
                        <SvgIcon fontSize='small'>
                            <UsersIcon />
                        </SvgIcon>
                    ),
                    permissionsRequired: [
                        Permission.CUSTOMER_NAV,
                        Permission.CUSTOMER_DETAIL_NAV,
                    ],
                    exact: true,
                },
                {
                    title: t('sidebar.tickets'),
                    path: '/tickets',
                    icon: (
                        <SvgIcon fontSize='small'>
                            <TicketIcon />
                        </SvgIcon>
                    ),
                    permissionsRequired: [
                        Permission.TICKETS_NAV,
                        Permission.TICKETS_CANCEL,
                    ],
                    exact: true,
                },
                {
                    title: t('sidebar.vouchers'),
                    path: '/vouchers',
                    icon: (
                        <SvgIcon fontSize='small'>
                            <GiftIcon />
                        </SvgIcon>
                    ),
                    permissionsRequired: [
                        Permission.TICKETS_NAV,
                        Permission.TICKETS_CANCEL,
                    ],
                    exact: true,
                },
            ],
        },
        {
            title: t('sidebar.products'),
            icon: (
                <SvgIcon fontSize='small'>
                    <TicketIcon />
                </SvgIcon>
            ),
            entries: [
                {
                    title: t('sidebar.specialTariffs'),
                    path: '/spezialtarife',
                    icon: (
                        <SvgIcon fontSize='small'>
                            <BankNotes />
                        </SvgIcon>
                    ),
                    permissionsRequired: [Permission.EXTERNAL_CODES_NAV],
                    exact: true,
                },
                {
                    title: t('sidebar.productConfiguration'),
                    path: '/produktkonfiguration',
                    icon: (
                        <SvgIcon fontSize='small'>
                            <NewspaperIcon />
                        </SvgIcon>
                    ),
                    permissionsRequired: [
                        Permission.LIFTOPERATOR_CONFIGURATION_NAV,
                    ],
                    exact: true,
                },
                {
                    title: t('sidebar.filterTags'),
                    path: '/produktkonfiguration/filtertags',
                    icon: (
                        <SvgIcon fontSize='small'>
                            <NewspaperIcon />
                        </SvgIcon>
                    ),
                    permissionsRequired: [
                        Permission.LIFTOPERATOR_CONFIGURATION_NAV,
                    ],
                    exact: true,
                },
                {
                    title: t('sidebar.tags'),
                    path: '/produktkonfiguration/tags',
                    icon: (
                        <SvgIcon fontSize='small'>
                            <NewspaperIcon />
                        </SvgIcon>
                    ),
                    permissionsRequired: [
                        Permission.LIFTOPERATOR_CONFIGURATION_NAV,
                    ],
                    exact: true,
                },
                {
                    title: t('sidebar.rulesets'),
                    path: '/regelwerke',
                    icon: (
                        <SvgIcon fontSize='small'>
                            <UserGroupIcon />
                        </SvgIcon>
                    ),
                    permissionsRequired: [Permission.CONDITION_LINKS_NAV],
                    exact: true,
                },
                {
                    title: t('sidebar.textCustomizations'),
                    path: '/text-anpassungen-ticket',
                    icon: (
                        <SvgIcon fontSize='small'>
                            <NewspaperIcon />
                        </SvgIcon>
                    ),
                    permissionsRequired: [Permission.TEXT_REPLACEMENT_NAV],
                    exact: true,
                },
            ],
        },
        {
            title: t('sidebar.webshopConfiguration'),
            icon: (
                <SvgIcon fontSize='small'>
                    <WrenchScrewdriverIcon />
                </SvgIcon>
            ),
            entries: [
                {
                    title: t('sidebar.mailConfiguration'),
                    path: '/mail-konfiguration',
                    icon: (
                        <SvgIcon fontSize='small'>
                            <NewspaperIcon />
                        </SvgIcon>
                    ),
                    permissionsRequired: [Permission.MAIL_CONFIGURATION_NAV],
                    exact: true,
                },
                {
                    title: t('sidebar.standardShops'),
                    path: '/standardshops',
                    icon: (
                        <SvgIcon fontSize='small'>
                            <UserGroupIcon />
                        </SvgIcon>
                    ),
                    permissionsRequired: [Permission.WEBSHOP_NAV],
                    exact: true,
                },
                {
                    title: t('sidebar.ticketTemplates'),
                    path: '/ticketvorlagen',
                    icon: (
                        <SvgIcon fontSize='small'>
                            <NewspaperIcon />
                        </SvgIcon>
                    ),
                    permissionsRequired: [Permission.MAIL_CONFIGURATION_NAV],
                    exact: true,
                },
                {
                    title: t('sidebar.faqs'),
                    path: '/faq',
                    icon: (
                        <SvgIcon fontSize='small'>
                            <QuestionMarkIcon />
                        </SvgIcon>
                    ),
                    permissionsRequired: [Permission.FAQ_NAV],
                    exact: true,
                },
                {
                    title: 'Gutscheinvorlagen',
                    path: '/gutscheinvorlagen',
                    icon: (
                        <SvgIcon fontSize='small'>
                            <DocumentDuplicateIcon />
                        </SvgIcon>
                    ),
                    permissionsRequired: [Permission.VOUCHER_TEMPLATE_NAV],
                    exact: true,
                },
                {
                    title: 'Gutscheine ausstellen',
                    path: '/gutscheine-ausstellen',
                    icon: (
                        <SvgIcon fontSize='small'>
                            <GiftTopIcon />
                        </SvgIcon>
                    ),
                    permissionsRequired: [Permission.VOUCHER_ISSUE],
                    exact: true,
                },
            ],
        },
        {
            title: t('sidebar.externalAccess'),
            icon: (
                <SvgIcon fontSize='small'>
                    <KeyIcon />
                </SvgIcon>
            ),
            entries: [
                {
                    title: t('sidebar.hospitality'),
                    path: '/hospitality',
                    icon: (
                        <SvgIcon fontSize='small'>
                            <NewspaperIcon />
                        </SvgIcon>
                    ),
                    permissionsRequired: [Permission.HOSPITALITY_NAV],
                    exact: true,
                    disabled: params?.disabledPaths?.includes('/hospitality'),
                },
                {
                    title: t('sidebar.b2b'),
                    path: '/b2b',
                    icon: (
                        <SvgIcon fontSize='small'>
                            <UserGroupIcon />
                        </SvgIcon>
                    ),
                    permissionsRequired: [Permission.WEBSHOP_NAV],
                    exact: true,
                    disabled: params?.disabledPaths?.includes('/b2b'),
                },
            ],
        },
        {
            title: t('sidebar.billing'),
            path: '/abrechnungen',
            icon: (
                <SvgIcon fontSize='small'>
                    <CurrencyEuroIcon />
                </SvgIcon>
            ),
            permissionsRequired: [Permission.BILLING_NAV],
        },
        {
            title: t('sidebar.userManagement'),
            path: '/benutzerverwaltung',
            icon: (
                <SvgIcon fontSize='small'>
                    <UserGroupIcon />
                </SvgIcon>
            ),
            permissionsRequired: [Permission.DATA_MANAGEMENT_NAV],
        },
        // Last entry by design: the widget editor is a standalone fullscreen
        // page with its own chrome, so it opens in a new tab and carries the
        // lift operator in the URL instead of reading it from the auth state.
        {
            title: t('sidebar.widgetEditor'),
            path: params?.liftOperatorId
                ? `/widget-editor?liftOperatorId=${params.liftOperatorId}`
                : '/widget-editor',
            permissionsRequired: [],
            external: true,
            icon: (
                <SvgIcon fontSize='small'>
                    <Squares2X2Icon />
                </SvgIcon>
            ),
        },
    ];
};

export const useB2bSidebarItems = (): AdminSidebarItem[] => {
    const { t } = useTranslation();

    return [
        {
            title: t('sidebar.dashboard'),
            icon: (
                <SvgIcon fontSize='small'>
                    <ChartBarIcon />
                </SvgIcon>
            ),
            entries: [
                {
                    title: t('sidebar.b2b'),
                    path: '/dashboard/b2b',
                    icon: null,
                    permissionsRequired: [Permission.STATISTIC_NAV],
                    exact: true,
                },
            ],
        },
        {
            title: t('sidebar.sales'),
            icon: (
                <SvgIcon fontSize='small'>
                    <ArrowTrendingUpIcon />
                </SvgIcon>
            ),
            entries: [
                {
                    title: t('sidebar.tickets'),
                    path: '/tickets',
                    icon: (
                        <SvgIcon fontSize='small'>
                            <ChartBarIcon />
                        </SvgIcon>
                    ),
                    permissionsRequired: [],
                    exact: true,
                },
                {
                    title: t('sidebar.orderTickets'),
                    path: '/ticket-bestellen',
                    icon: (
                        <SvgIcon fontSize='small'>
                            <ChartBarIcon />
                        </SvgIcon>
                    ),
                    permissionsRequired: [],
                    exact: true,
                },
            ],
        },
    ];
};
